/**
 * Editor render + zoom cluster — page render, overlay render, zoom math,
 * `ZoomDropdown` UI controller. (`FocusTrap` moved on to ../a11y-view/focus-trap.ts.)
 *
 * Extracted from editor.ts in Phase EB.2 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §"Phase EB.2"
 * + §3 Cluster D + §9.1 — coupling discoveries). Phase EB.3 (§9.3) added
 * `renderPageIntoFrame` + `renderOverlaysForPage` here too and consolidated
 * the overlay render path with `renderOverlays` via a shared private
 * `_renderOverlaysCore` helper — the multi-page manager's per-page renders
 * and the current-page render now share one source of truth for overlay
 * geometry, role-map computation, and MCID-marker delegation. With both
 * `renderPageIntoFrame` and `renderOverlaysForPage` co-located in this
 * module, the EB.2 `_registerCanvasRender` seam is no longer load-bearing
 * for multipage — it's retained as an export only to keep the WeakMap
 * encapsulated (any future caller that needs to participate in per-canvas
 * cancel tracking goes through this helper, not via a raw WeakMap import).
 *
 * Cross-cluster coupling (post-EB.5; setter reduced from 3 → 1 binding):
 *
 *   Phase EB.5 (Cluster F extraction) moved `applyPowerEditorOp` and
 *   `nudgeReadingOrder` into `editor-card-pane.ts`. Those two callbacks
 *   are now direct imports via a namespace (lazy) — see `_cp` below —
 *   so the card-pane ↔ render cycle is deterministic.
 *
 *   `selectStruct` REMAINS in editor.ts (orchestrator scope — it touches
 *   `els.structList`). The `setOverlayCallbackBindings({selectStruct})`
 *   setter is therefore reduced from 3 bindings to 1: only `selectStruct`
 *   still flows through the injection seam. The setter remains the
 *   second cross-cluster coupling pattern alongside EB.1's
 *   callback-at-call-site approach (showWorkspace) — chosen because
 *   `renderOverlays` is called from too many sites to thread the
 *   callback through every signature.
 *
 * `_renderTask`, `_canvasRenderTasks` and `_overlayCbBindings` are
 * module-scope singletons. `state` and `els` come from `editor-state.js`.
 * `_bboxCache`, `getBoundingBoxesForPageCached`, `prefetchRange` also come
 * from editor-state (promoted there in Phase EB.1 — design doc §9.1).
 *
 * Per the "Type-in-place before decomposing" rule (CLAUDE.md): this
 * extraction preserves the existing `any` annotations on the still-untyped
 * pdf.js boundaries — tightening them is the `ts-strict-zero` Epic's job.
 */
import { log } from "./log.js";
import { renderOverlays as renderOverlayBoxes, buildRoleMap, roleAbbrev as overlayRoleAbbrev, } from "./overlays.js";
import { openTypePopover } from "./cards.js";
import { buildRoleMapFromIr } from "./interop.js";
import { modeFromEvent } from "./multi-select.js";
import { renderMcidOverlay, clearMcidOverlay } from "./mcid-overlay.js";
import { projectionFromTransform } from "./page-projection.js";
import { PageZone } from "./multi-page-scroll.js";
import { state, els, _bboxCache, getBoundingBoxesForPageCached, } from "./editor-state.js";
import { filterAndOverrideBboxesAgainstIr, computeContainerSuppressedStructIds, findIrCardStructIdsMissingBox } from "./editor-bbox-utils.js";
// `applyPowerEditorOp` + `nudgeReadingOrder` live in `editor-card-pane.ts`
// (Cluster F, extracted in Phase EB.5). The namespace-import shape below
// breaks the render ↔ card-pane ESM cycle: property accesses on `_cp`
// resolve at call time (inside `buildOverlayCallbacks`), by which point
// both modules are fully evaluated. This is the same pattern editor-insert-flow.ts
// uses for its card-pane dependency.
import * as _cp from "./editor-card-pane.js";
import { ZOOM_MIN, ZOOM_MAX, clampScale, fitToWidthScale as _fitToWidthScalePure, fitToPageScale as _fitToPageScalePure, zoomStepUp, zoomStepDown, } from "./zoom-logic.js";
import { EditorPageRasterCache, editorPictureRenderEnabled, } from "./editor-render-strategy.js";
// ─── Zoom constants ─────────────────────────────────────────────────────────
//
// ZOOM_MIN, ZOOM_MAX are imported from zoom-logic.ts (pure module) and
// re-exported so existing callers of editor-render.ts continue to work.
// The `import` above makes them available in this file; TypeScript treats
// imported bindings as implicitly re-exported when also declared `export`.
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-exported constants
export { ZOOM_MIN, ZOOM_MAX };
// Performance: zoom-driven overlay re-render is debounced ~150ms.
// Each call increments _overlayRenderSeq; only the most-recent fetch's
// result is allowed to mutate the DOM, so an in-flight request from a
// stale zoom level cancels itself on completion.
const OVERLAY_ZOOM_DEBOUNCE_MS = 150;
// ─── Render-strategy seam (pdfjs-editor-removal-cutover Phase 2, chunk 2a) ───
//
// Behind `ADA_EDITOR_PICTURE_RENDER` (default OFF → byte-identical pdf.js), the
// page raster + natural dims come from the backend `/api/pdf-page-image` PNGs
// instead of pdf.js. The natural dims are derived FROM the fetched raster (its
// CropBox-framed pixel dims ÷ dpi), so the overlay coordinate frame keys off the
// EXACT raster the overlays sit on — the C4-correct registration guarantee. The
// cache is lazy per-page (caveat C1). See editor-render-strategy.ts.
const _rasterCache = new EditorPageRasterCache();
/**
 * Natural (scale=1) page dims in PDF points — the overlay-frame unit. Flag OFF
 * (or flag ON before the page's raster is cached): pdf.js `getViewport({scale:1})`,
 * byte-identical to the pre-port behaviour. Flag ON with the raster cached: the
 * C4-correct rotation-swapped CropBox dims derived from the backend PNG.
 */
function _naturalPageDims(pageNum, page) {
    if (editorPictureRenderEnabled() && state.jobId) {
        const cached = _rasterCache.cachedNaturalDims(state.jobId, pageNum);
        if (cached)
            return cached;
    }
    const naturalVp = page.getViewport({ scale: 1 });
    return { width: naturalVp.width, height: naturalVp.height };
}
// ─── Zoom helpers ──────────────────────────────────────────────────────────
/**
 * Compute the fit-to-width scale for the current canvas-pane width.
 * Reads container width from the DOM and delegates the pure math to
 * zoom-logic.ts#fitToWidthScale. `pageNum` (default: current page) keys the
 * render-strategy natural-dims lookup so a fit computed for an off-current page
 * (the multi-page manager) uses THAT page's frame.
 */
export function fitToWidthScale(page, pageNum = state.currentPage) {
    const dims = _naturalPageDims(pageNum, page);
    return _fitToWidthScalePure(dims.width, els.pdfScroll.clientWidth);
}
/**
 * Compute the fit-to-page scale so the whole page fits vertically too.
 * Reads container dimensions from the DOM and delegates the pure math to
 * zoom-logic.ts#fitToPageScale.
 */
export function fitToPageScale(page, pageNum = state.currentPage) {
    const dims = _naturalPageDims(pageNum, page);
    return _fitToPageScalePure(dims.width, dims.height, els.pdfScroll.clientWidth, els.pdfScroll.clientHeight);
}
function updateZoomUI(scale) {
    const pct = `${Math.round(scale * 100)}%`;
    // WCAG 2.5.3: aria-label must contain the visible label as a substring.
    // The two zoom controls are disambiguated by prefix so screen readers
    // can distinguish them (U7-v3 uniqueness requirement).
    els.statusZoom.textContent = pct;
    els.statusZoom.setAttribute("aria-label", `Status bar zoom: ${pct} — click to change`);
    // Mirror zoom level in the pane-header pill's text span.
    if (els.paneZoomLabel) {
        els.paneZoomLabel.textContent = pct;
    }
    if (els.paneZoomLevel) {
        els.paneZoomLevel.setAttribute("aria-label", `Pane zoom level: ${pct} — click to change`);
    }
    // Highlight the active preset in both dropdowns.
    const pctNum = Math.round(scale * 100);
    for (const btn of els.zoomDropdown.querySelectorAll("button[data-zoom]")) {
        const v = btn.dataset.zoom ?? "";
        const active = (v !== "fit-width" && v !== "fit-page" && parseInt(v, 10) === pctNum);
        btn.classList.toggle("is-active", active);
    }
    if (els.paneZoomMenu) {
        for (const btn of els.paneZoomMenu.querySelectorAll("button[data-zoom]")) {
            btn.classList.toggle("is-active", parseInt(btn.dataset.zoom ?? "", 10) === pctNum);
        }
    }
}
/** Apply a new scale (or NaN for fit-to-width). Re-renders current page.
 *  Pass fitToPane=true to track the "fit to pane" mode so resize events
 *  can recompute the scale. */
export async function applyZoom(newScale, { fitToPane = false } = {}) {
    if (!state.pdfDoc)
        return;
    state.currentScale = newScale;
    state.fitToPaneMode = fitToPane;
    // Invalidate per-page bbox cache and the data-prefetched canvas flags so
    // overlays re-fetch and canvases re-render at the new scale. Predicate 2
    // of the lazy-load redesign: applyZoom MUST clear the bbox cache before
    // renderPage. (See docs/design/editor-lazy-load-redesign-260508.md §10.)
    _bboxCache.clear();
    if (Array.isArray(state.pageDescriptors)) {
        for (const desc of state.pageDescriptors) {
            if (desc?.canvasEl)
                desc.canvasEl.dataset.prefetched = "";
        }
    }
    await renderPage(state.currentPage);
    // Doc-wide zoom (bug4, docable-editor-overlay-render-260910): renderPage
    // above rescales ONLY the current page. In multi-page mode drive a
    // document-wide reflow so every other page frame + its canvas/overlays
    // rescale too. Reached through `state` (set by mountMultiPageScroll) rather
    // than a static import — editor-multipage imports THIS module, so a static
    // back-import would be an ESM cycle. Null on single-page / pre-mount paths.
    if (state.multiPageReflow) {
        await state.multiPageReflow();
    }
}
export async function zoomIn() {
    if (!state.pdfDoc)
        return;
    const page = await state.pdfDoc.getPage(state.currentPage);
    const baseScale = Number.isNaN(state.currentScale) ? fitToWidthScale(page) : state.currentScale;
    await applyZoom(zoomStepUp(baseScale));
}
export async function zoomOut() {
    if (!state.pdfDoc)
        return;
    const page = await state.pdfDoc.getPage(state.currentPage);
    const baseScale = Number.isNaN(state.currentScale) ? fitToWidthScale(page) : state.currentScale;
    await applyZoom(zoomStepDown(baseScale));
}
// ─── Render ─────────────────────────────────────────────────────────────────
// In-flight PDF.js render task. WHY: pinch-zoom on Mac trackpads (and rapid
// Ctrl+wheel) fires many `wheel` events in quick succession, each calling
// applyZoom → renderPage. Without cancellation, multiple PDF.js render tasks
// share the same canvas context and write into it concurrently — PDF.js
// applies its own affine transform per-render to draw glyph paths, and a
// second task starting mid-paint can leave the canvas in a state where
// already-painted content appears mirrored or rotated (the "PDF flips
// upside-down at a particular zoom level" symptom). PDF.js's RenderTask
// API exposes `.cancel()` for exactly this reason.
let _renderTask = null;
// Per-canvas in-flight render-task tracking. PDF.js throws "Cannot use the
// same canvas during multiple render() operations" when two render() calls
// hit the same canvas concurrently. Both `renderPage` (single-page) and
// `renderPageIntoFrame` (multi-page manager) cancel any in-flight task on
// the SAME canvas before starting a new one; per-canvas keying is required
// because the multi-page manager spreads renders across N canvases.
const _canvasRenderTasks = new WeakMap();
export function _cancelExistingRender(canvasEl) {
    const prior = _canvasRenderTasks.get(canvasEl);
    if (prior) {
        try {
            prior.cancel();
        }
        catch { /* PDF.js throws on cancel-after-completion */ }
        _canvasRenderTasks.delete(canvasEl);
    }
}
/**
 * Register an in-flight PDF.js render task against a canvas, so a later
 * `_cancelExistingRender` call can cancel it. Phase EB.3 moved both
 * single-page (`renderPage`) and multi-page (`renderPageIntoFrame`) render
 * paths into this module, so the WeakMap is no longer load-bearing for
 * cross-module collaboration. This export is retained as the public seam
 * any future external caller would go through, keeping `_canvasRenderTasks`
 * module-private (no raw WeakMap exposed to importers).
 *
 * @param canvasEl Canvas element the task is rendering into.
 * @param task PDF.js RenderTask (`{ cancel(): void; promise: Promise<...> }`).
 */
export function _registerCanvasRender(canvasEl, task) {
    _canvasRenderTasks.set(canvasEl, task);
}
/**
 * Configure a canvas element and its enclosing frame for a given PDF.js
 * viewport, applying device-pixel-ratio scaling.
 *
 * Sets canvas pixel dimensions, CSS display dimensions, frame CSS dimensions,
 * and initialises the 2D context transform. Returns the 2D context.
 */
export function _configureCanvasForViewport(canvasEl, frameEl, viewport) {
    const dpr = window.devicePixelRatio || 1; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    canvasEl.width = Math.floor(viewport.width * dpr);
    canvasEl.height = Math.floor(viewport.height * dpr);
    canvasEl.style.width = `${viewport.width}px`;
    canvasEl.style.height = `${viewport.height}px`;
    frameEl.style.width = `${viewport.width}px`;
    frameEl.style.height = `${viewport.height}px`;
    const ctx = canvasEl.getContext("2d");
    if (ctx === null) {
        throw new Error("Could not acquire 2D context for canvas");
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx, dpr };
}
export async function renderPage(pageNum, _retryGuard = 0) {
    state.currentPage = pageNum;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: renderPage only called after Tier-1 assigns pdfDoc
    const page = await state.pdfDoc.getPage(pageNum);
    // Render-strategy seam (chunk 2a): flag ON → fetch the backend PNG for this
    // page BEFORE computing scale, so the fit-to-width scale + the stored natural
    // dims both key off the C4-correct raster frame on the FIRST paint (not just
    // after a zoom). A null frame (fetch miss) falls back to the pdf.js path.
    let rasterFrame = null;
    if (editorPictureRenderEnabled() && state.jobId) {
        rasterFrame = await _rasterCache.ensurePageRaster(state.jobId, pageNum);
    }
    // Use explicit scale if set, else compute fit-to-width.
    const scale = Number.isNaN(state.currentScale)
        ? fitToWidthScale(page)
        : clampScale(state.currentScale);
    // WHY: when the workspace transitions visible (showWorkspace() ran but the
    // browser hasn't completed layout yet), pdfScroll.clientWidth reads 0 and
    // fitToWidthScale collapses to ZOOM_MIN, rendering the canvas at ~10% of
    // natural size (visually blank). Defer one frame so the browser paints,
    // then retry once. Threshold 10px because anything narrower is degenerate
    // (display:none on the pane) and retrying further would loop.
    if (_retryGuard === 0 && Number.isNaN(state.currentScale)
        && scale <= ZOOM_MIN && els.pdfScroll && els.pdfScroll.clientWidth < 10) {
        await new Promise((r) => requestAnimationFrame(r));
        return renderPage(pageNum, 1);
    }
    const viewport = page.getViewport({ scale });
    state.pageViewport = viewport;
    // Render into the per-page frame descriptor when available; fall back
    // to the legacy els.canvas / els.pageframe (e.g., before
    // mountMultiPageScroll has fired, or during unit tests).
    const desc = state.pageDescriptors[pageNum - 1];
    const canvasEl = (desc?.canvasEl) ?? els.canvas;
    const frameEl = (desc?.frame) ?? els.pageframe;
    // Carry the page's projection on the descriptor (design §3.3). Sourced from
    // the SAME viewport that paints the canvas (Ph1b G-3 — one transform source
    // for both the pdf.js and raster paths), so overlays can never disagree with
    // the raster frame. The overlay + draw-to-create paths consume this instead
    // of re-deriving a crop/rotation-blind frame from the DOM dataset.
    if (desc)
        desc.projection = projectionFromTransform(viewport.transform);
    // Store the natural (scale=1) page dimensions on the canvas so sync.js
    // can compute the user-space → pixel-space transform without accessing state.
    // Flag ON keys these off the C4-correct raster frame (see _naturalPageDims).
    const nd = _naturalPageDims(pageNum, page);
    canvasEl.dataset.pageNaturalWidth = String(nd.width);
    canvasEl.dataset.pageNaturalHeight = String(nd.height);
    // Keep the legacy canvas element in sync too (sync.js reads it directly).
    els.canvas.dataset.pageNaturalWidth = String(nd.width);
    els.canvas.dataset.pageNaturalHeight = String(nd.height);
    if (rasterFrame) {
        // Flag ON — paint the backend PNG. Size the canvas from the C4-correct
        // natural dims × scale (so canvas CSS width == the frame the overlay
        // scale at `_renderOverlaysCore` divides by), then draw the raster. NO
        // pdf.js render task, so nothing to cancel/await; a prior task on this
        // canvas (e.g. a flag flip mid-session) is cancelled defensively.
        _cancelExistingRender(canvasEl);
        const paintViewport = { width: nd.width * scale, height: nd.height * scale };
        const { ctx } = _configureCanvasForViewport(canvasEl, frameEl, paintViewport);
        ctx.drawImage(rasterFrame.img, 0, 0, paintViewport.width, paintViewport.height);
    }
    else {
        const { ctx } = _configureCanvasForViewport(canvasEl, frameEl, viewport);
        // Cancel any in-flight render — including ones started by the multi-
        // page manager via renderPageIntoFrame, which uses the same canvas.
        _cancelExistingRender(canvasEl);
        if (_renderTask) {
            try {
                _renderTask.cancel();
            }
            catch {
                // PDF.js throws on cancel-after-completion; safe to ignore.
            }
        }
        _renderTask = page.render({ canvasContext: ctx, viewport, transform: null });
        _canvasRenderTasks.set(canvasEl, _renderTask);
        try {
            await _renderTask.promise;
        }
        catch (err) {
            // PDF.js raises a "RenderingCancelledException" when cancel() is
            // called mid-flight; that's expected during rapid zoom and not an
            // error worth propagating.
            if (!err || err.name !== "RenderingCancelledException") {
                throw err;
            }
            return;
        }
        finally {
            _renderTask = null;
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: pdfDoc confirmed non-null above via getPage() call which would have thrown
    const totalPages = state.pdfDoc.numPages;
    els.pageIndicator.textContent = `Page ${pageNum} of ${totalPages}`;
    els.statusPage.textContent = `Page ${pageNum} / ${totalPages}`;
    updateZoomUI(scale);
    // Scroll multi-page manager to this page if a jump was requested
    // (e.g., toolbar prev/next page nav).
    if (state.multiPageMgr) {
        void state.multiPageMgr.scrollToPage(pageNum);
    }
    await renderOverlays();
}
/**
 * Cross-browser idle-callback wrapper. `requestIdleCallback` is undefined on
 * Safari ≤ 15 (still in some iOS devices); fall back to a short setTimeout
 * so the post-Tier1 prefetch still defers, just without idle-deadline
 * cooperation. 50 ms is the de-facto polyfill cadence used across the JS
 * ecosystem (long enough to let the page-1 paint commit; short enough that
 * the prefetch isn't visibly stalled).
 */
export function _scheduleIdle(fn) {
    if (typeof window !== "undefined"
        && typeof window.requestIdleCallback === "function") { // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        window.requestIdleCallback(fn); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    }
    else {
        setTimeout(fn, 50);
    }
}
let _overlayCbBindings = null;
/**
 * Install the orchestrator callback. Must be called from editor.ts AFTER
 * `selectStruct` is defined and BEFORE the first overlay render fires
 * (which means before the first `renderPage` / `loadFile` invocation).
 * Re-callable for tests that need to swap bindings.
 */
export function setOverlayCallbackBindings(bindings) {
    _overlayCbBindings = bindings;
}
function _requireOverlayCbBindings() {
    if (_overlayCbBindings === null) {
        throw new Error("editor-render: overlay callback bindings not installed — "
            + "editor.ts must call setOverlayCallbackBindings(...) before "
            + "the first renderOverlays() / renderPage() call.");
    }
    return _overlayCbBindings;
}
/**
 * Build the overlay-bbox callbacks shared by `renderOverlays` (current-page
 * render — always-active, called on every page change and op commit) and
 * `renderOverlaysForPage` (lazy multi-page variant, called by the scroll
 * manager for off-current pages). Single source of truth so click-to-select,
 * badge-click, reorder-click, and locked-snap behavior cannot diverge
 * (per editor-formal-structure-audit-260507.md Finding 2). Phase EB.3
 * consolidated both call sites onto `_renderOverlaysCore`, so the "single
 * source of truth" is now structural, not merely conventional.
 */
export function buildOverlayCallbacks() {
    const cbs = _requireOverlayCbBindings();
    return {
        onSelect: (structId, ev) => {
            const mode = modeFromEvent(ev);
            // Shift/Cmd-click extends or toggles; plain click is single.
            if (state.linkedViewsCtrl) {
                state.linkedViewsCtrl.selectStructId(structId, mode);
                if (mode === "single") {
                    state.linkedViewsCtrl.scrollCardIntoView(structId);
                    state.linkedViewsCtrl.flashCard(structId);
                }
            }
            // Keep the single-select state in sync only when the click is a
            // single-select; toggle/range may yield 0 or N items.
            if (mode === "single")
                cbs.selectStruct(structId);
        },
        onBadgeClick: (structId, ev) => {
            const card = state.cardByStructId?.get(structId);
            if (!card)
                return;
            const anchor = (ev?.currentTarget ?? ev?.target);
            if (!anchor)
                return;
            openTypePopover({
                anchor,
                currentRole: card.roleToken,
                onPick: (newRole) => {
                    if (newRole === card.roleToken)
                        return;
                    void _cp.applyPowerEditorOp({
                        kind: "set-role",
                        structId,
                        before: card.roleToken,
                        after: newRole,
                    });
                },
            });
        },
        onReorderClick: (structId, dir) => _cp.nudgeReadingOrder(structId, dir),
        // Pending inserts whose rubber-band snapped on all four sides are
        // tracked in pendingInsertStore with snappedAllSides = true. Surface
        // to the overlay renderer so the corresponding .editor-overlay-bbox
        // carries `.is-locked-snap` (no resize handles).
        lockedSnapByStructId: (id) => {
            const entry = state.pendingInsertStore?.get
                ? state.pendingInsertStore.get(id) : null;
            return !!(entry?.snappedAllSides);
        },
    };
}
/**
 * Shared overlay-render core used by both `renderOverlays` (current page;
 * seq-guarded + legacy fallback) and `renderOverlaysForPage` (multi-page
 * lazy renderer; no seq-guard). Phase EB.3 DRY consolidation — see design
 * doc §9.3. Resolves the page's `PageProjection` (descriptor.projection, with a
 * current-page `state.pageViewport` fallback for the pre-mount path) and feeds
 * it to both the struct-overlay and MCID-overlay renderers.
 *
 * Caller responsibilities:
 *   - resolve `overlayEl` (current-page falls back to legacy `els.overlay`;
 *     per-page reads from `state.pageDescriptors`);
 *   - decide whether the early-return precondition is `state.pageViewport`
 *     (current-page) or `state.interop` (per-page);
 *   - decide whether to install + check the `_overlayRenderSeq` cancel-guard.
 *
 * Returns true when overlays were rendered, false when an early-return
 * (precondition / no-projection / seq-stale / WASM failure) fired.
 */
async function _renderOverlaysCore(pageNum, overlayEl, useSeqGuard) {
    const seq = useSeqGuard ? ++state._overlayRenderSeq : -1;
    let boxes;
    try {
        boxes = await getBoundingBoxesForPageCached(pageNum);
    }
    catch (err) {
        // WASM call failed (e.g. interop not yet ready, or PDF parse error).
        // Clear any stale overlay boxes so the view isn't misleading; the
        // next renderPage() will retry.
        overlayEl.innerHTML = "";
        log.warn(`[editor] getBoundingBoxesForPageCached page ${pageNum} failed:`, err);
        return false;
    }
    // Bail if a newer overlay render kicked off during the WASM round-trip.
    if (useSeqGuard && seq !== state._overlayRenderSeq)
        return false;
    // The projection is the SOLE frame authority (design §3.3) — carried on the
    // descriptor by the paint (renderPage / renderPageIntoFrame), never re-derived
    // from the DOM dataset (that crop/rotation-blind re-read was bug-3's root).
    const desc = state.pageDescriptors[pageNum - 1];
    let projection = desc?.projection ?? null;
    // Legacy current-page path (no descriptor: pre-mount / single-page / tests):
    // state.pageViewport carries the same transform. Off-current pages always
    // have a descriptor (the multi-page manager creates them), so this only fires
    // for the current page.
    if (!projection && pageNum === state.currentPage && state.pageViewport) {
        projection = projectionFromTransform(state.pageViewport.transform);
    }
    if (!projection) {
        // Frame not yet painted — nothing to project against. Clear so no stale
        // boxes linger; the next paint carries the projection.
        overlayEl.innerHTML = "";
        return false;
    }
    // Do NOT bail when structTree is absent (bug #3, 260813): an UNTAGGED /
    // empty document, or the degraded backend-IR path (struct-tree derive timed
    // out → structTree null), still authors structure by DRAWING. The IR-only
    // boxes (draw-create commits) are merged in below via
    // filterAndOverrideBboxesAgainstIr Rule 3, so the drawn box must render even
    // with no server struct tree.
    //
    // Resilience (editor-list-rca all-badges-"?" crash class): when the
    // server-derived struct tree is absent/stale, derive the role map from the
    // local IR (the client source of truth between saves) rather than leaving it
    // GLOBALLY empty. A globally-empty map blanks EVERY badge to "?"; deriving
    // from the IR keeps each badge showing its role, and a single underived
    // block degrades to "?" for THAT badge only.
    if (!state.overlayRoleMap) {
        const treeNodes = state.structTree?.nodes ?? [];
        state.overlayRoleMap = (treeNodes.length > 0)
            ? buildRoleMap(treeNodes)
            : buildRoleMapFromIr(state.ir);
    }
    // The WASM bbox cache reflects the SERVER-SIDE struct tree. Local
    // client-only ops (delete-element, set-bbox, create-element) skip
    // the WASM round-trip, so the WASM list still contains entries for
    // locally-deleted structs (and the bboxes for locally-moved structs
    // are stale). Filter and override against the IR — the local IR is
    // the source of truth between save dispatches.
    const filteredBoxes = filterAndOverrideBboxesAgainstIr(boxes, state.ir, pageNum);
    // Suppress the LEFT-pane boxes of a List container's internal-layout
    // children (the stacked list-item boxes) so only the one wrapping List box
    // draws; the items stay editable on the right (editor-structure-overlay-ux-260904).
    const suppressedStructIds = computeContainerSuppressedStructIds(state.ir, pageNum);
    // Bug 5 (INV-OVL-1, USER DIRECTIVE): an element whose card lists on page P
    // with no renderable box at all must stay LOCATABLE, not silently absent
    // (rule #8). For each carded, on-page, non-suppressed element missing from
    // the reconciled box list, append a degenerate placeholder; overlays.ts
    // EXPANDS it to a visible locate-affordance. Log (never swallow) the set.
    const boxlessStructIds = findIrCardStructIdsMissingBox(filteredBoxes, state.ir, pageNum, suppressedStructIds);
    if (boxlessStructIds.length > 0) {
        log.warn(`[editor] page ${pageNum}: ${boxlessStructIds.length} carded element(s) have no renderable box — rendering locate-affordance(s):`, boxlessStructIds);
        for (const id of boxlessStructIds) {
            filteredBoxes.push({ structId: id, page: pageNum, bbox: [0, 0, 0, 0] });
        }
    }
    renderOverlayBoxes({
        container: overlayEl,
        boxes: filteredBoxes,
        roleByStructId: state.overlayRoleMap,
        projection,
        selectedStructId: state.selectedStructId,
        selectedStructIds: state.selectedStructIds,
        ...buildOverlayCallbacks(),
        visible: state.overlaysVisible,
        readingOrderByStructId: state.readingOrderByStructId,
        structIdsWithLongDescNext: state.structIdsWithLongDescNext,
        cardByStructId: state.cardByStructId,
        suppressedStructIds,
    });
    // MCID diagnostic overlay (Phase 6 of editor-mcid-overlay-260507.md).
    // Renders dot markers + convex-hull polygons over the struct overlay so
    // misalignment between MCID positions and parent struct bboxes is
    // visible at a glance. Toggle is OFF by default; rendering only fires
    // when the user has enabled it AND the IR carries `mcids`.
    if (state.mcidOverlayVisible
        && state.ir && Array.isArray(state.ir.mcids)
        && state.ir.mcids.length > 0) {
        try {
            renderMcidOverlay({
                mcids: state.ir.mcids,
                pageNum,
                container: overlayEl,
                projection,
                roleByStructId: state.overlayRoleMap,
                structsById: state.structTree?.byId ?? null,
            });
        }
        catch (err) {
            log.warn(`[editor] renderMcidOverlay page ${pageNum} failed:`, err);
        }
    }
    return true;
}
export async function renderOverlays() {
    // Render into the per-page overlay element when available.
    const desc = state.pageDescriptors[state.currentPage - 1];
    const overlayEl = (desc?.overlayEl) ?? els.overlay;
    // structTree may be null on an untagged / empty doc or the degraded IR path;
    // still render so IR-authored (draw-create) boxes appear (bug #3, 260813).
    if (!state.pageViewport || !state.overlaysVisible) {
        // Clear immediately without a WASM round-trip. When not visible, skipping
        // the getBoundingBoxesForPage call avoids a spurious throw that would
        // leave stale overlay boxes in the DOM (the silent-catch bug that made
        // the toggle appear dead).
        overlayEl.innerHTML = "";
        return;
    }
    await _renderOverlaysCore(state.currentPage, overlayEl, /*useSeqGuard*/ true);
    // Clear the legacy single-page els.overlay alias when rendering into a
    // per-page overlay; otherwise its stale bboxes would show through and
    // duplicate the active page's content.
    if (overlayEl !== els.overlay) {
        els.overlay.innerHTML = "";
    }
}
/**
 * Render overlays for a specific (potentially off-current) page into its
 * per-page overlay element. Called from the MultiPageScrollManager's
 * renderOverlays hook (wired in `editor-multipage.ts`).
 *
 * Differs from `renderOverlays` in three ways: (a) takes an explicit
 * `pageNum` rather than reading `state.currentPage`; (b) precondition is
 * `state.interop` (multi-page manager fires before `state.pageViewport`
 * is established for the per-page strip); (c) no `_overlayRenderSeq`
 * guard — per-page renders are independent of one another, the manager
 * polices its own concurrency by zone-promotion.
 *
 * The `_frame` parameter is currently unused; preserved as a callable
 * shape so the manager wiring stays a 1-line lambda. Phase EB.3 §9.3.
 */
export async function renderOverlaysForPage(pageNum, _frame) {
    // structTree-null tolerated so IR-authored (draw-create) boxes render on an
    // untagged / empty doc or the degraded IR path (bug #3, 260813).
    if (!state.overlaysVisible || !state.interop)
        return;
    const desc = state.pageDescriptors[pageNum - 1];
    if (!desc)
        return;
    const { overlayEl } = desc;
    await _renderOverlaysCore(pageNum, overlayEl, /*useSeqGuard*/ false);
}
/**
 * Render a single page's PDF.js canvas directly into the per-page frame.
 * Called from the MultiPageScrollManager's renderCanvas hook (wired in
 * `editor-multipage.ts`).
 *
 * Co-located with `renderPage` so the WeakMap `_canvasRenderTasks` stays a
 * true module-private singleton: both the single-page render path and the
 * multi-page-manager path register/cancel through the same WeakMap, so a
 * `renderPage` cancel-and-restart will not race against a
 * `renderPageIntoFrame` task. Phase EB.3 §9.3 moved this here from
 * editor.ts (was the Cluster E body) and retired the load-bearing role of
 * the `_registerCanvasRender` export.
 */
export async function renderPageIntoFrame(pageNum, frame) {
    if (!state.pdfDoc)
        return;
    const page = await state.pdfDoc.getPage(pageNum); // narrowed non-null by guard above
    // Render-strategy seam (chunk 2a): flag ON → fetch the backend PNG for this
    // page first so the scale + natural dims key off the C4-correct frame.
    let rasterFrame = null;
    if (editorPictureRenderEnabled() && state.jobId) {
        rasterFrame = await _rasterCache.ensurePageRaster(state.jobId, pageNum);
    }
    const scale = Number.isNaN(state.currentScale)
        ? fitToWidthScale(page, pageNum)
        : clampScale(state.currentScale);
    const viewport = page.getViewport({ scale });
    const desc = state.pageDescriptors[pageNum - 1];
    if (!desc)
        return;
    const { canvasEl } = desc;
    // Same single-source projection as renderPage (Ph1b G-3): the transform is
    // renderer-independent, so both the pdf.js and raster branches below carry
    // the same frame authority on the descriptor.
    desc.projection = projectionFromTransform(viewport.transform);
    const nd = _naturalPageDims(pageNum, page);
    canvasEl.dataset.pageNaturalWidth = String(nd.width);
    canvasEl.dataset.pageNaturalHeight = String(nd.height);
    if (rasterFrame) {
        // Flag ON — paint the backend PNG (no pdf.js render task).
        _cancelExistingRender(canvasEl);
        const paintViewport = { width: nd.width * scale, height: nd.height * scale };
        const { ctx } = _configureCanvasForViewport(canvasEl, frame, paintViewport);
        ctx.drawImage(rasterFrame.img, 0, 0, paintViewport.width, paintViewport.height);
        return;
    }
    const { ctx } = _configureCanvasForViewport(canvasEl, frame, viewport);
    // Cancel any in-flight render on this canvas — shares the WeakMap with
    // `renderPage` so the two paths never collide on a single canvas.
    _cancelExistingRender(canvasEl);
    const task = page.render({ canvasContext: ctx, viewport, transform: null });
    _canvasRenderTasks.set(canvasEl, task);
    try {
        await task.promise;
    }
    catch (err) {
        if (!err || err.name !== "RenderingCancelledException")
            throw err;
    }
}
/**
 * Debounced overlay re-render. Use on rapid events (zoom wheel, pinch).
 * The page-render path calls renderOverlays() directly so the first paint
 * is still fast (<100ms target).
 */
export function renderOverlaysDebounced() {
    if (state._overlayDebounceTimer) {
        clearTimeout(state._overlayDebounceTimer);
    }
    state._overlayDebounceTimer = setTimeout(() => {
        state._overlayDebounceTimer = null;
        // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget inside setTimeout callback; cannot await in sync callback
        renderOverlays().catch(() => { });
    }, OVERLAY_ZOOM_DEBOUNCE_MS);
}
// Compatibility shim — sidebar role abbreviation reuses the overlay helper.
export function roleAbbrev(role) {
    return overlayRoleAbbrev(role);
}
// ─── ZoomDropdown controller ───────────────────────────────────────────────
//
// Owns open/close state for one zoom dropdown (trigger + menu pair).
// Replaces the module-scope _zoomDropdownOpen / _paneZoomMenuOpen booleans.
// Wiring (click handlers, keyboard nav, applyZoom calls) lives outside this
// class so each dropdown can express its own preset-selection logic. The
// status-bar + pane-header dropdown instances are created in editor.ts.
export class ZoomDropdown {
    _triggerEl;
    _menuEl;
    _open;
    constructor(triggerEl, menuEl) {
        this._triggerEl = triggerEl;
        this._menuEl = menuEl;
        this._open = false;
    }
    isOpen() { return this._open; }
    open() {
        this._menuEl.classList.remove("hidden");
        this._triggerEl.setAttribute("aria-expanded", "true");
        this._open = true;
    }
    close() {
        this._menuEl.classList.add("hidden");
        this._triggerEl.setAttribute("aria-expanded", "false");
        this._open = false;
    }
}
// `FocusTrap` now lives in ../a11y-view/focus-trap.ts (DRY Epic §C.7 #14 — the SR
// panels became the third consumer, forcing the shared home). It lived here
// historically (extracted in EB.2 alongside ZoomDropdown), passed through
// modal-logic.ts, and no render-cluster code consumes it.
// ─── Phase EC: zoom-dropdown instances + overlay/MCID toggle UI ────────────
//
// The two ZoomDropdown instances + their wiring, the syncOverlaysToggleUI /
// syncMcidToggleUI helpers + their button click handlers, the toolbar zoom
// buttons, the Ctrl+wheel zoom-to-cursor handler, and the resize-driven
// fit-to-width/fit-to-pane re-render moved here from editor.ts in Phase EC
// (design doc §"Phase EC" + §9.6). EB.2 had hosted the ZoomDropdown class
// here but kept the instances + per-button wiring in the orchestrator
// because the dropdowns reach into sibling clusters (state.pdfDoc for
// pdfDoc-gated zoom math). EC cashes in the deferred move: every consumer
// of these handlers lives inside editor-render, so the handlers belong here
// too. Side effects on module import: addEventListener calls on the toolbar
// zoom buttons, the two dropdown trigger/menu pairs, the pdfScroll wheel
// handler, the window resize handler + ResizeObserver on the pdfScroll
// pane, and a document-level click handler that closes the dropdowns on
// outside click.
// ─── Toggle UI helpers (overlays + MCID) ────────────────────────────────────
/**
 * Toggle button: flips state.overlaysVisible, persists to localStorage,
 * re-renders the current page so the change is immediate.
 *
 * Stays in editor-render because the toggle predicate routes through
 * renderOverlays(); the click handler is wired at module init below.
 */
export function syncOverlaysToggleUI() {
    if (!els.overlaysToggle)
        return;
    const on = !!state.overlaysVisible;
    els.overlaysToggle.setAttribute("aria-pressed", on ? "true" : "false");
    els.overlaysToggle.classList.toggle("is-on", on);
    // Mirror the persisted state onto the workspace so the toggle takes
    // effect across all per-page overlay containers immediately on boot.
    if (els.workspace) {
        els.workspace.classList.toggle("is-overlays-hidden", !on);
    }
}
/**
 * Sync the MCID-positions toggle UI: pressed state, on/off class, disabled
 * state when `state.ir.mcids` is absent (no data → toggle is visible-disabled
 * with a tooltip explaining the unavailability).
 *
 * Exported so the card-pane `mountCardPane(deps)` deps bundle can take it
 * (rebuildCardLookups in editor-card-pane.ts calls syncMcidToggleUI through
 * the deps bundle — see design doc §9.5).
 */
export function syncMcidToggleUI() {
    if (!els.mcidToggle)
        return;
    const hasData = !!(state.ir && Array.isArray(state.ir.mcids) && state.ir.mcids.length > 0);
    const on = !!state.mcidOverlayVisible && hasData;
    els.mcidToggle.setAttribute("aria-pressed", on ? "true" : "false");
    els.mcidToggle.classList.toggle("is-on", on);
    if (hasData) {
        els.mcidToggle.removeAttribute("disabled");
        els.mcidToggle.removeAttribute("aria-disabled");
        els.mcidToggle.title = "Show or hide diagnostic content-marker overlays";
    }
    else {
        els.mcidToggle.setAttribute("disabled", "true");
        els.mcidToggle.setAttribute("aria-disabled", "true");
        els.mcidToggle.title = "MCID data not loaded";
    }
    if (els.workspace) {
        els.workspace.classList.toggle("is-mcid-hidden", !on);
    }
}
// Initial paint at module-init so the toolbar reflects persisted state.
syncOverlaysToggleUI();
syncMcidToggleUI();
if (els.overlaysToggle) {
    els.overlaysToggle.addEventListener("click", () => {
        void (async () => {
            state.overlaysVisible = !state.overlaysVisible;
            try {
                localStorage.setItem("editor:overlays", String(state.overlaysVisible));
            } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            catch { /* localStorage unavailable; live setting still applies for this session */ }
            syncOverlaysToggleUI();
            // Multi-page mode renders each page's overlays into its own descriptor.
            // renderOverlays() only acts on the current page, so toggling off would
            // leave other pages' overlays visible. Use a workspace-level class +
            // CSS rule to hide all overlay boxes uniformly without re-rendering.
            if (els.workspace) {
                els.workspace.classList.toggle("is-overlays-hidden", !state.overlaysVisible);
            }
            await renderOverlays();
        })();
    });
}
/**
 * The 1-based page numbers whose frame is in the OVERLAYS zone (promoted, fully
 * painted). The MCID toggle-ON path re-renders each of these so markers appear
 * across the whole visible strip, not just the current page.
 *
 * The zone MUST be compared against the `PageZone.OVERLAYS` constant, never a
 * bare `"OVERLAYS"` literal: the multi-page manager writes the LOWERCASE
 * `PageZone.OVERLAYS` (= "overlays") to `frame.dataset.zone`, so an uppercase
 * literal never matched and the multi-page re-render was silently inert (the
 * zone-case bug, design §6).
 */
export function _promotedOverlayPageNums(descriptors) {
    const out = [];
    descriptors.forEach((desc, i) => {
        if (desc?.frame?.dataset?.zone === PageZone.OVERLAYS)
            out.push(i + 1);
    });
    return out;
}
if (els.mcidToggle) {
    els.mcidToggle.addEventListener("click", () => {
        void (async () => {
            // Disabled state — defensive guard so a synthesized click can't toggle
            // when there's no data to show.
            if (els.mcidToggle.hasAttribute("disabled"))
                return;
            state.mcidOverlayVisible = !state.mcidOverlayVisible;
            try {
                localStorage.setItem("editor:mcidOverlay", String(state.mcidOverlayVisible));
            } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            catch { /* localStorage unavailable; live setting still applies for this session */ }
            syncMcidToggleUI();
            // The workspace class hides MCID layer instantly via CSS; we still
            // need a render call when toggling ON because the markers may not
            // yet exist for the current page.
            if (state.mcidOverlayVisible) {
                await renderOverlays();
                // Multi-page mode: also re-render currently-promoted pages
                // beyond the current one so MCID markers appear across the visible
                // strip, not just on the active page.
                if (state.multiPageMgr && Array.isArray(state.pageDescriptors)) {
                    for (const pageNum of _promotedOverlayPageNums(state.pageDescriptors)) {
                        const desc = state.pageDescriptors[pageNum - 1];
                        if (!desc)
                            continue;
                        try {
                            await renderOverlaysForPage(pageNum, desc.frame);
                        }
                        catch { /* logged inside renderOverlaysForPage */ }
                    }
                }
            }
            else {
                // Toggle is now OFF — remove MCID markers from ALL overlay containers
                // so specs and accessibility tools see 0 markers (not just CSS-hidden
                // ones). clearMcidOverlay is idempotent and a no-op on containers that
                // have no markers, so calling it on all pages is safe.
                clearMcidOverlay(els.overlay);
                if (state.multiPageMgr && Array.isArray(state.pageDescriptors)) {
                    for (const desc of state.pageDescriptors) {
                        if (desc?.overlayEl)
                            clearMcidOverlay(desc.overlayEl);
                    }
                }
            }
        })();
    });
}
// ─── Zoom dropdown instances + click/keyboard wiring ─────────────────────
// Status-bar zoom dropdown.
const _statusZoomDropdown = new ZoomDropdown(els.statusZoom, els.zoomDropdown);
/**
 * Test-only / orchestrator-only accessor for the status-bar dropdown.
 * The global keydown handler (editor-modals-save.ts) reaches for this
 * to close the dropdown on Esc.
 */
export function getStatusZoomDropdown() { return _statusZoomDropdown; }
// Open + focus the first/active option so keyboard users land inside the
// listbox. Mirror of openPaneZoomMenu() below — both dropdowns share the
// same keyboard contract per frontend-audit F-2 (260512).
function openStatusZoomDropdown() {
    _statusZoomDropdown.open();
    const active = (els.zoomDropdown.querySelector("li[role='option'] button.is-active")
        ?? els.zoomDropdown.querySelector("li[role='option'] button"));
    if (active)
        active.focus();
}
els.statusZoom.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (_statusZoomDropdown.isOpen()) {
        _statusZoomDropdown.close();
    }
    else {
        openStatusZoomDropdown();
    }
});
// Preset buttons inside the dropdown.
els.zoomDropdown.addEventListener("click", (ev) => {
    void (async () => {
        const btn = ev.target.closest("button[data-zoom]");
        if (!btn)
            return;
        const v = btn.dataset.zoom ?? "";
        _statusZoomDropdown.close();
        if (v === "fit-width") {
            await applyZoom(NaN);
        }
        else if (v === "fit-page") {
            if (state.pdfDoc) {
                const page = await state.pdfDoc.getPage(state.currentPage);
                await applyZoom(fitToPageScale(page));
            }
        }
        else {
            await applyZoom(parseInt(v, 10) / 100);
        }
    })();
});
// Keyboard navigation within the status-bar zoom listbox. The visible
// focusable items are the inner buttons inside each `<li role="option">`.
// Mirrors the pane-zoom menu handler below (ArrowUp/Down/Home/End/Esc/Enter)
// per frontend-audit F-2 (260512). The custom-zoom input row is skipped here
// because that row has its own input-specific keydown handler (Enter→Go, Esc).
els.zoomDropdown.addEventListener("keydown", (ev) => {
    const items = Array.from(els.zoomDropdown.querySelectorAll("li[role='option'] button[data-zoom]"));
    if (items.length === 0)
        return;
    const idx = items.indexOf(document.activeElement); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (ev.key === "ArrowDown") {
        ev.preventDefault();
        // WHY: loop invariant — (idx + 1 + items.length) % items.length is always in [0, length-1]
        items[(idx + 1 + items.length) % items.length].focus();
    }
    else if (ev.key === "ArrowUp") {
        ev.preventDefault();
        // WHY: loop invariant — (idx - 1 + items.length) % items.length is always in [0, length-1]
        items[(idx - 1 + items.length) % items.length].focus();
    }
    else if (ev.key === "Home") {
        ev.preventDefault();
        items[0].focus(); // WHY: items.length > 0 guard above
    }
    else if (ev.key === "End") {
        ev.preventDefault();
        items[items.length - 1].focus(); // WHY: items.length > 0 guard above
    }
    else if (ev.key === "Escape") {
        ev.stopPropagation();
        _statusZoomDropdown.close();
        els.statusZoom.focus();
    }
    else if (ev.key === "Enter" && idx >= 0) {
        // Browsers fire `click` on a focused button when Enter is pressed,
        // so the existing click handler picks it up. Just close on activation.
        // (No preventDefault — let the click fire.)
    }
});
// Custom zoom go button.
els.zoomCustomGo.addEventListener("click", () => {
    void (async () => {
        const v = parseInt(els.zoomCustomInput.value, 10);
        if (Number.isFinite(v) && v >= 10 && v <= 500) {
            _statusZoomDropdown.close();
            await applyZoom(v / 100);
        }
    })();
});
els.zoomCustomInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter")
        els.zoomCustomGo.click();
    if (ev.key === "Escape")
        _statusZoomDropdown.close();
    ev.stopPropagation();
});
// Toolbar +/- buttons (issue 7a).
els.zoomInBtn.addEventListener("click", () => { void zoomIn(); });
els.zoomOutBtn.addEventListener("click", () => { void zoomOut(); });
// Sidebar mirror zoom buttons.
els.sbZoomInBtn.addEventListener("click", () => { void zoomIn(); });
els.sbZoomOutBtn.addEventListener("click", () => { void zoomOut(); });
// Pane-header zoom dropdown — opens downward so the user can pick presets
// without scrolling to the status bar at the bottom.
const _paneZoomDropdown = new ZoomDropdown(els.paneZoomLevel, els.paneZoomMenu);
/** Test-only / orchestrator-only accessor for the pane-header dropdown. */
export function getPaneZoomDropdown() { return _paneZoomDropdown; }
function openPaneZoomMenu() {
    _paneZoomDropdown.open();
    // Focus the active item (or the first item if none is active).
    const active = (els.paneZoomMenu.querySelector("button.is-active")
        ?? els.paneZoomMenu.querySelector("button[role='menuitem']"));
    if (active)
        active.focus();
}
if (els.paneZoomLevel) {
    els.paneZoomLevel.addEventListener("click", (ev) => {
        ev.stopPropagation();
        if (_paneZoomDropdown.isOpen()) {
            _paneZoomDropdown.close();
        }
        else {
            openPaneZoomMenu();
        }
    });
}
if (els.paneZoomMenu) {
    // Preset button clicks.
    els.paneZoomMenu.addEventListener("click", (ev) => {
        void (async () => {
            const btn = ev.target.closest("button[data-zoom]");
            if (!btn)
                return;
            _paneZoomDropdown.close();
            await applyZoom(parseInt(btn.dataset.zoom ?? "", 10) / 100);
        })();
    });
    // Keyboard navigation within the menu (arrow keys, Escape, Enter).
    els.paneZoomMenu.addEventListener("keydown", (ev) => {
        const items = Array.from(els.paneZoomMenu.querySelectorAll("button[role='menuitem']"));
        if (items.length === 0)
            return;
        const idx = items.indexOf(document.activeElement); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (ev.key === "ArrowDown") {
            ev.preventDefault();
            // WHY: loop invariant — (idx + 1) % items.length is always in [0, length-1]
            items[(idx + 1) % items.length].focus();
        }
        else if (ev.key === "ArrowUp") {
            ev.preventDefault();
            // WHY: loop invariant — (idx - 1 + items.length) % items.length is always in [0, length-1]
            items[(idx - 1 + items.length) % items.length].focus();
        }
        else if (ev.key === "Escape") {
            ev.stopPropagation();
            _paneZoomDropdown.close();
            els.paneZoomLevel.focus();
        }
        else if (ev.key === "Home") {
            ev.preventDefault();
            items[0].focus(); // WHY: items.length > 0 guard above
        }
        else if (ev.key === "End") {
            ev.preventDefault();
            items[items.length - 1].focus(); // WHY: items.length > 0 guard above
        }
    });
}
// "Fit to pane" button → fit-to-page (tightest fit), persistent on resize.
if (els.fitPaneBtn) {
    els.fitPaneBtn.addEventListener("click", () => {
        void (async () => {
            if (!state.pdfDoc)
                return;
            const page = await state.pdfDoc.getPage(state.currentPage);
            await applyZoom(fitToPageScale(page), { fitToPane: true });
        })();
    });
}
// Close both zoom dropdowns on outside click.
document.addEventListener("click", () => {
    if (_statusZoomDropdown.isOpen())
        _statusZoomDropdown.close();
    if (_paneZoomDropdown.isOpen())
        _paneZoomDropdown.close();
});
// ─── Ctrl+wheel zoom-to-cursor (issue 7c) ─────────────────────────────────
els.pdfScroll.addEventListener("wheel", (ev) => {
    void (async () => {
        if (!ev.ctrlKey && !ev.metaKey)
            return;
        ev.preventDefault();
        if (!state.pdfDoc)
            return;
        const page = await state.pdfDoc.getPage(state.currentPage);
        const baseScale = Number.isNaN(state.currentScale) ? fitToWidthScale(page) : state.currentScale;
        // delta > 0 = wheel down = zoom out; delta < 0 = wheel up = zoom in.
        const factor = ev.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, baseScale * factor));
        await applyZoom(newScale);
    })();
}, { passive: false });
// ─── Resize re-render — keep fit-to-width / fit-to-pane accurate ──────────
//
// Takes the orchestrator's `applyLayout` callback because the pane-layout
// singleton lives in editor.ts (orchestrator scope — see design doc §9.1
// for the matching `showWorkspace(applyLayout)` shape). editor.ts calls
// `installResizeReflow(applyLayout)` once at boot.
let _resizeTimer = null;
let _resizeApplyLayout = null;
async function _onResizeRerender() {
    // Re-apply pane layout so pixel widths stay correct.
    if (_resizeApplyLayout)
        _resizeApplyLayout();
    if (!state.pdfDoc)
        return;
    if (Number.isNaN(state.currentScale)) {
        // Fit-to-width mode: re-render with the new container width.
        await renderPage(state.currentPage);
    }
    else if (state.fitToPaneMode) {
        // Fit-to-pane mode: recompute the tightest-fit scale for the new pane size.
        const page = await state.pdfDoc.getPage(state.currentPage);
        await applyZoom(fitToPageScale(page), { fitToPane: true });
    }
}
const PANE_RESIZE_DEBOUNCE_MS = 80;
/**
 * Wire window-resize + pane-resize-observer re-render hooks. The
 * `applyLayout` callback is the orchestrator-owned pane-layout reflow
 * (editor.ts's `_paneLayout.applyLayout()`). Idempotent — re-calling
 * just swaps the stored callback.
 */
export function installResizeReflow(applyLayout) {
    _resizeApplyLayout = applyLayout;
}
window.addEventListener("resize", () => {
    if (_resizeTimer !== null)
        clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(_onResizeRerender, 150);
});
// Also trigger a fit-to-pane recompute when the PDF pane is resized via the
// splitter drag. The pane-layout controller changes pane widths directly via
// flex-basis; a ResizeObserver on the PDF scroll container fires whenever that
// changes, including during a splitter drag session.
if (typeof ResizeObserver !== "undefined" && els.pdfScroll) {
    const _paneResizeObserver = new ResizeObserver(() => {
        if (!state.pdfDoc || !state.fitToPaneMode)
            return;
        if (_resizeTimer !== null)
            clearTimeout(_resizeTimer);
        _resizeTimer = setTimeout(_onResizeRerender, PANE_RESIZE_DEBOUNCE_MS);
    });
    _paneResizeObserver.observe(els.pdfScroll);
    window.addEventListener("pagehide", () => {
        try {
            _paneResizeObserver.disconnect();
        }
        catch { /* best-effort cleanup */ }
    }, { once: true });
}
